/**
 * Stock Adjustment Form Component
 * Modal form for creating/editing stock adjustments with scrollable content
 */

import React, { useState, useEffect } from 'react';
import { useInventory } from '../../../contexts/InventoryContext';
import adminApi from '../../../services/adminApi';
import inventoryApi from '../../../services/inventoryApi';
import SearchableSelect from '../../Common/SearchableSelect';

const StockAdjustmentForm = ({ stockAdjustment, viewMode = false, onSuccess, onCancel }) => {
  const { items, updateStockLevel } = useInventory();

  const [formData, setFormData] = useState({
    item: '',
    warehouse: '',
    adjustmentType: 'increase', // 'increase', 'decrease', 'set'
    quantity: '',
    newQuantity: '', // For 'set' type
    reason: '',
    reasonCode: 'manual_adjustment',
    notes: '',
    reference: '',
    costAdjustment: '',
    unitCost: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStock, setCurrentStock] = useState(0);
  const [warehouses, setWarehouses] = useState([]);

  const loadWarehouses = async () => {
    try {
      // Get all locations and filter for warehouse type
      const response = await adminApi.getLocations({ type: 'warehouse', limit: 1000 });
      // Handle different response structures
      const warehousesData = response.data?.data || response.data || [];
      setWarehouses(warehousesData);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      // Fallback: get all locations without type filter
      try {
        const fallbackResponse = await adminApi.getLocations({ limit: 1000 });
        const allLocations = fallbackResponse.data?.data || fallbackResponse.data || [];
        const warehouses = allLocations.filter(location => location.type === 'warehouse');
        setWarehouses(warehouses);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        setWarehouses([]);
      }
    }
  };

  useEffect(() => {
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (stockAdjustment) {
      setFormData({
        item: stockAdjustment.item?._id || stockAdjustment.item || '',
        warehouse: stockAdjustment.warehouse?._id || stockAdjustment.warehouse || '',
        adjustmentType: stockAdjustment.adjustmentType || 'increase',
        quantity: stockAdjustment.quantity?.toString() || '',
        newQuantity: stockAdjustment.newQuantity?.toString() || '',
        reason: stockAdjustment.reason || '',
        reasonCode: stockAdjustment.reasonCode || 'manual_adjustment',
        notes: stockAdjustment.notes || '',
        reference: stockAdjustment.reference || '',
        costAdjustment: stockAdjustment.costAdjustment?.toString() || '',
        unitCost: stockAdjustment.unitCost?.toString() || ''
      });
    }
  }, [stockAdjustment]);

  // Fetch current stock when item/warehouse changes
  useEffect(() => {
    const fetchCurrentStock = async () => {
      if (formData.item && formData.warehouse) {
        try {
          const stockParams = {
            item: formData.item,
            warehouse: formData.warehouse,
            limit: 1000
          };

          const stockResponse = await inventoryApi.getStockLevels(stockParams);

          // Stock for an item/warehouse can be split across multiple StockLevel
          // documents (one per location, e.g. from GRN receipts) — sum them all
          // rather than reading a single arbitrary record.
          const records = stockResponse.data || [];
          const totalQuantity = records.reduce((sum, s) => sum + (s.quantity || 0), 0);
          setCurrentStock(totalQuantity);
        } catch (error) {
          console.error('Error fetching current stock:', error);
          setCurrentStock(0);
        }
      } else {
        setCurrentStock(0);
      }
    };

    fetchCurrentStock();
  }, [formData.item, formData.warehouse]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.item) {
      newErrors.item = 'Item is required';
    }

    if (!formData.warehouse) {
      newErrors.warehouse = 'Warehouse is required';
    }

    if (formData.adjustmentType === 'set') {
      if (!formData.newQuantity || formData.newQuantity < 0) {
        newErrors.newQuantity = 'New quantity must be 0 or greater';
      }
    } else {
      if (!formData.quantity || formData.quantity <= 0) {
        newErrors.quantity = 'Adjustment quantity must be greater than 0';
      }
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    if (formData.costAdjustment && formData.costAdjustment < 0) {
      newErrors.costAdjustment = 'Cost adjustment cannot be negative';
    }

    if (formData.unitCost && formData.unitCost < 0) {
      newErrors.unitCost = 'Unit cost cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Trim string values for select fields to prevent whitespace issues
    const cleanValue = (name === 'adjustmentType' || name === 'reasonCode') && typeof value === 'string'
      ? value.trim()
      : value;

    setFormData(prev => ({
      ...prev,
      [name]: cleanValue
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare submission data for StockAdjustment API
      const submitData = {
        item: formData.item,
        warehouse: formData.warehouse,
        location: formData.location || '',
        adjustmentType: formData.adjustmentType.trim(),
        reason: formData.reason.trim(),
        reasonCode: formData.reasonCode.trim(),
        notes: formData.notes.trim(),
        reference: formData.reference.trim()
      };

      // Add quantity based on adjustment type
      if (formData.adjustmentType === 'set') {
        submitData.newQuantity = parseFloat(formData.newQuantity);
      } else {
        submitData.quantity = parseFloat(formData.quantity);
      }

      // Add optional numeric fields
      if (formData.unitCost && !isNaN(parseFloat(formData.unitCost)) && parseFloat(formData.unitCost) >= 0) {
        submitData.unitCost = parseFloat(formData.unitCost);
      }

      // Create stock adjustment (will be pending approval)
      const response = await inventoryApi.createStockAdjustment(submitData);
      onSuccess();
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: error.response?.data?.message || error.message || 'Failed to adjust stock' });
    } finally {
      setLoading(false);
    }
  };

  const adjustableItems = ((items && Array.isArray(items)) ? items : [])
    .filter(item => item.type !== 'asset' && item.inventory?.trackInventory !== false);

  const itemOptions = adjustableItems.map(item => ({
    value: item._id,
    label: `${item.name} (${item.sku})`
  }));

  const warehouseOptions = ((warehouses && Array.isArray(warehouses)) ? warehouses : []).map(warehouse => ({
    value: warehouse._id,
    label: `${warehouse.name} (${warehouse.code})`
  }));

  const reasonCodeOptions = [
    { value: 'manual_adjustment', label: 'Manual Adjustment' },
    { value: 'damaged_goods', label: 'Damaged Goods' },
    { value: 'lost_items', label: 'Lost Items' },
    { value: 'found_items', label: 'Found Items' },
    { value: 'theft', label: 'Theft' },
    { value: 'expiry', label: 'Expired Items' },
    { value: 'quality_control', label: 'Quality Control' },
    { value: 'inventory_count', label: 'Inventory Count Correction' },
    { value: 'production_use', label: 'Production Use' },
    { value: 'other', label: 'Other' }
  ];

  const adjustmentTypeOptions = [
    { value: 'increase', label: 'Increase Stock' },
    { value: 'decrease', label: 'Decrease Stock' },
    { value: 'set', label: 'Set Exact Amount' }
  ];

  const calculateNewStock = () => {
    if (formData.adjustmentType === 'set') {
      return parseFloat(formData.newQuantity) || 0;
    } else if (formData.adjustmentType === 'increase') {
      return currentStock + (parseFloat(formData.quantity) || 0);
    } else if (formData.adjustmentType === 'decrease') {
      return Math.max(0, currentStock - (parseFloat(formData.quantity) || 0));
    }
    return currentStock;
  };

  const field = (label, name, type = 'text', opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={opts.placeholder || ''}
        disabled={viewMode || opts.disabled}
        step={opts.step}
        min={opts.min}
        className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ${
          errors[name] ? 'border-red-300 bg-red-50' : 'border-gray-300'
        } ${(viewMode || opts.disabled) ? 'bg-gray-100' : ''} ${opts.right ? 'text-right' : ''}`}
      />
      {errors[name] && <p className="text-[10px] text-red-500 mt-0.5">{errors[name]}</p>}
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const select = (label, name, options, opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <SearchableSelect
        name={name}
        value={formData[name]}
        onChange={handleChange}
        options={options}
        placeholder={opts.placeholder || `Select ${label.toLowerCase()}…`}
        disabled={viewMode || opts.disabled}
        error={errors[name] || ''}
        size="sm"
      />
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const textarea = (label, name, opts = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <textarea
        name={name}
        rows={opts.rows || 3}
        value={formData[name]}
        onChange={handleChange}
        placeholder={opts.placeholder || ''}
        disabled={viewMode}
        className={`w-full px-2 py-1.5 text-xs border rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none ${
          errors[name] ? 'border-red-300 bg-red-50' : 'border-gray-300'
        } ${viewMode ? 'bg-gray-100' : ''}`}
      />
      {errors[name] && <p className="text-[10px] text-red-500 mt-0.5">{errors[name]}</p>}
      {opts.help && <p className="text-[10px] text-gray-400 mt-0.5">{opts.help}</p>}
    </div>
  );

  const sectionHeader = (title) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider">{title}</span>
      <div className="flex-1 h-px bg-indigo-100" />
    </div>
  );

  const formIcon = (
    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white shadow-2xl border-0 flex flex-col rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                {formIcon}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white" id="modal-title">
                  {viewMode ? 'View Stock Adjustment' : (stockAdjustment ? 'Edit Stock Adjustment' : 'Create Stock Adjustment')}
                </h3>
                <p className="text-indigo-100 text-sm mt-1">
                  {viewMode ? 'Review stock adjustment details' : (stockAdjustment ? 'Update stock adjustment details' : 'Add a new stock adjustment record')}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all duration-200"
              onClick={onCancel}
            >
              <span className="sr-only">Close</span>
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 pt-3 pb-2 space-y-4">
            {errors.submit && (
              <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded px-3 py-2">{errors.submit}</p>
            )}

            {/* Adjustment Details */}
            {sectionHeader('Adjustment Details')}
            <div className="grid grid-cols-3 gap-2">
              {select('Item', 'item', itemOptions, { required: true })}
              {select('Warehouse', 'warehouse', warehouseOptions, { required: true })}
              {select('Adjustment Type', 'adjustmentType', adjustmentTypeOptions, { required: true })}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {formData.adjustmentType === 'set'
                ? field('Set Quantity To', 'newQuantity', 'number', { required: true, placeholder: '0', min: '0', step: '0.01', help: 'The exact quantity to set the stock to' })
                : field(`${formData.adjustmentType === 'increase' ? 'Increase' : 'Decrease'} By`, 'quantity', 'number', { required: true, placeholder: '0', min: '0', step: '0.01', help: `Quantity to ${formData.adjustmentType} stock by` })}
              {select('Reason Code', 'reasonCode', reasonCodeOptions, { required: true })}
            </div>

            {/* Stock Summary */}
            {formData.item && formData.warehouse && (
              <div className="grid grid-cols-3 gap-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <div>
                  <span className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider">Current Stock</span>
                  <span className="text-sm font-bold text-gray-900">{currentStock}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider">Change</span>
                  <span className={`text-sm font-bold ${formData.adjustmentType === 'decrease' ? 'text-red-600' : 'text-indigo-600'}`}>
                    {formData.adjustmentType === 'set'
                      ? `Set to ${formData.newQuantity || 0}`
                      : `${formData.adjustmentType === 'increase' ? '+' : '-'}${formData.quantity || 0}`}
                  </span>
                </div>
                <div>
                  <span className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider">New Stock</span>
                  <span className="text-sm font-bold text-green-700">{calculateNewStock()}</span>
                </div>
              </div>
            )}

            {/* Additional Information */}
            {sectionHeader('Additional Information')}
            {textarea('Reason', 'reason', { required: true, rows: 2, placeholder: 'Detailed reason for the stock adjustment…', help: 'Provide a clear explanation for this adjustment' })}

            <div className="grid grid-cols-2 gap-2">
              {textarea('Notes', 'notes', { rows: 2, placeholder: 'Additional notes…', help: 'Any additional information about this adjustment' })}
              {field('Reference Number', 'reference', 'text', { placeholder: 'REF-001', help: 'External reference number or document' })}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {field('Unit Cost', 'unitCost', 'number', { placeholder: '0.00', step: '0.01', min: '0', right: true, help: 'Cost per unit' })}
              {field('Cost Adjustment', 'costAdjustment', 'number', { placeholder: '0.00', step: '0.01', right: true, help: 'Total cost impact' })}
            </div>
          </div>

          {/* Footer with actions */}
          <div className="flex-shrink-0 bg-white px-6 py-4 border-t border-gray-200 shadow-lg">
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="px-8 py-3 border border-gray-300 rounded-xl text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm"
                onClick={onCancel}
              >
                {viewMode ? 'Close' : 'Cancel'}
              </button>
              {!viewMode && (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-indigo-600 border border-transparent rounded-xl text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{stockAdjustment ? 'Updating...' : 'Creating...'}</span>
                    </div>
                  ) : (
                    stockAdjustment ? 'Update Adjustment' : 'Create Adjustment'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustmentForm;
